<template>
	<div>
		<v-list two-line class="pt-0 pb-0">
			<v-subheader v-if="title">
				<v-btn @click="collapsed = !collapsed" small icon>
					<v-icon>{{collapsed ? $vuetify.icons.values.next : $vuetify.icons.values.expand}}</v-icon>
				</v-btn>
				{{title}} ({{$formatter.decimal(items.length, {unit: 'st', allowZero: true, decimalPlaces: 0})}})
			</v-subheader>
			<template v-for="item in items">
				<v-list-item
					v-show="!collapsed"
					:key="item.FtgId"
					@click="$emit('click', item)"
					:disabled="item.disabled"
				>
					<v-list-item-avatar>
						<CompanyTableAvatar :item="item" :tile="true" :canEdit="false" size-no-image="42px"/>
					</v-list-item-avatar>
					<v-list-item-content>
						<v-list-item-title>
							{{item.Namn}}
						</v-list-item-title>
						<v-list-item-subtitle>{{$formatter.formatPersNrOrgNrTo12(item.Orgnr, {dash: true, serialNr: false}).slice(2)}} - {{item.FtgId}}</v-list-item-subtitle>
					</v-list-item-content>
					<v-list-item-action>
						<slot name="action" v-bind="{item}">
							<v-row dense>
								<v-col v-if="showRestoreInfo">
									<Tooltip v-if="item.RestoreInfo" :text="getRestoreInfoText(item.RestoreInfo)" allow-h-t-m-l>
										<v-btn icon @click.stop.prevent="() => {}" :color="getRestoreInfoIconColor(item.RestoreInfo)">
											<v-icon>{{getRestoreInfoIcon(item.RestoreInfo)}}</v-icon>
										</v-btn>
									</Tooltip>
								</v-col>
								<v-col v-if="cloneCompanyEnabled && !item.Archived">
									<Tooltip text="Kopiera företag">
										<v-btn icon color="accent" @click.stop.prevent="onShowCopyFtgDialog(item.FtgId)"><v-icon>mdi-content-copy</v-icon></v-btn>
									</Tooltip>
								</v-col>
								<v-col v-if="['LIC', 'ADM', 'FTG'].includes(UserAccount.Roll)">
									<Tooltip :text="item.Archived ? 'Återför företag från arkiv' : 'Arkivera företag'">
										<v-btn icon text color="accent" @click.stop.prevent="onToggleArchive(item)"><v-icon>{{item.Archived ? 'mdi-archive-cancel' : 'mdi-archive'}}</v-icon></v-btn>
									</Tooltip>
								</v-col>
								<v-col v-if="showFavourite">
									<Tooltip :text="item.isFavourite ? 'Avmarkera favorit' : 'Markera favorit'">
										<v-btn icon text :color="item.isFavourite ? 'accent' : 'secondary'" @click.stop="toggleFavourite(item)"><v-icon>{{item.isFavourite ? 'mdi-star' : 'mdi-star-outline'}}</v-icon></v-btn>
									</Tooltip>
								</v-col>
							</v-row>
						</slot>
					</v-list-item-action>
				</v-list-item>
			</template>
		</v-list>

		<Dialog v-if="cloneCompanyEnabled" ref="copyDialog" title="Kopiera företag" :persistent="isCopying">
			<v-container fluid>
				<v-row dense>
					<v-col cols="12">
						<SimpleTextInput v-model="targetFtgId" label="Nytt företags-id" :counter="15"/>
					</v-col>
					<v-col cols="12">
						<v-alert border="top" colored-border type="info" elevation="2" dense class="caption">
							Observera att följande inställningar <b>inte</b> kopieras till det nya företaget:
							<ul>
								<li>Standard behörighetsgrupp för företaget</li>
								<li>Användarkonton</li>
								<li>Åtkomst för företagsadministratörer</li>
							</ul>
						</v-alert>
					</v-col>
					<v-fade-transition hide-on-leave>
						<v-col cols="12" v-if="isCopying" class="pt-5">
							{{copyStatusMessage}}
							<v-progress-linear indeterminate height="15"/>
						</v-col>
					</v-fade-transition>
				</v-row>
			</v-container>

			<template v-slot:buttons="{hide}">
				<v-btn color="secondary" @click="hide" :disabled="isCopying">Avbryt</v-btn>
				<v-spacer></v-spacer>
				<v-btn color="primary" @click="onCopy" :loading="isCopying">Kopiera</v-btn>
			</template>
		</Dialog>
	</div>
</template>

<script>
import moment                 from 'moment';
import {mapActions, mapState} from 'vuex';
import LocalStorage           from '../lib/LocalStorage.js';
import Tooltip                from './Tooltip.vue';
import CompanyTableAvatar     from './CompanySettings/CompanyTableAvatar.vue';
import Dialog                 from './Dialog.vue';
import SimpleTextInput        from './SimpleTextInput.vue';

const RESTORE_INFO_DATE_LIMIT = 14;

export default {
	name: 'SelectCompanyList',

	props: {
		items: {
			type:     Array,
			required: true,
		},

		title: {
			type:    String,
			default: '',
		},

		showFavourite: {
			type:    Boolean,
			default: true,
		},
	},
	
	data: () => ({
		originalFtgId:     '',
		targetFtgId:       '',
		isCopying:         false,
		copyStatusMessage: '',
		collapsed:         false,
	}),

	components: {
		SimpleTextInput,
		Dialog,
		CompanyTableAvatar,
		Tooltip,
	},

	methods: {
		...mapActions(['confirmDialog', 'setUpdatedFtgInfo']),

		getRestoreInfoText({Time, User, AzStorageContainer, AzFileName, InfoText}) {
			const AzFileNameParts = AzFileName.split('_').reverse();

			AzFileNameParts.shift();

			const isPastDateAllowance = this.getRestoreInfoIconColor(...arguments) === 'error';

			return `
<b>Företaget är en inläst kopia</b>
<hr/>
<table>
<tbody>
<tr>
<th class="text-left">Licensnummer</th>
<th class="text-right">${this.$formatter.escape(AzStorageContainer)}</th>
</tr>
<tr>
<th class="text-left">Företagsid</th>
<th class="text-right">${this.$formatter.escape(AzFileNameParts.reverse().join('_'))}</th>
</tr>
<tr>
<th class="text-left">Tidpunkt</th>
<th class="text-right">${this.$formatter.momentFormat(Time, 'YYYY-MM-DD HH:mm')}</th>
</tr>
<tr>
<th class="text-left">Användare</th>
<th class="text-right">${this.$formatter.escape(User)}</th>
</tr>
${InfoText ? '<tr><th colspan="2" class="text-left">Notering</th></tr>' : ''}
${InfoText ? `<tr><td colspan="2" class="text-left">${this.$formatter.escape(InfoText)}</td></tr>` : ''}
${isPastDateAllowance ? '<tr><th colspan="2" class="text-left">Varning</th></tr>' : ''}
${isPastDateAllowance ? `<tr><td colspan="2" class="text-left">Dags att ta bort företaget om ärendet är avslutat (> 14 dgr)</td></tr>` : ''}
</tbody>
</table>
`.trim();
		},

		getRestoreInfoIcon() {
			return this.getRestoreInfoIconColor(...arguments) === 'error'
				? 'mdi-alert-circle'
				: 'mdi-information-outline';
		},

		getRestoreInfoIconColor({Time}) {
			const diff = moment().diff(moment(Time), 'days');

			return diff > RESTORE_INFO_DATE_LIMIT ? 'error' : 'accent';
		},

		onToggleArchive(item) {
			const {Archived, Namn, FtgId, Orgnr} = item;

			this.$tryCatch({
				task: async () => {
					if(!Archived) {
						await new Promise((resolve, reject) => {
							this.confirmDialog({
								title: 'Arkivera företag',
								text:  [
									`Är du säker på att du vill arkivera företaget <b>${this.$formatter.escape(Namn || FtgId || Orgnr)}</b>?`,
									'Arkivering döljer endast företaget från den primära listan av valbara företag.',
									'Du kommer fortfarande kunna komma åt företaget och alla eventuella användare som är direkt kopplade till företaget kommer fortfarande kunna logga in och registrera underlag.',
									'Ansvaret faller på dig som administratör att även inaktivera användarkonton och integrationer kopplade till företaget om så önskas.',
								].join('<br/><br/>'),
								cancelText:   'Avbryt',
								confirmColor: 'error',
								confirmText:  'Arkivera',
								onCancel:     () => reject(new Error('UserCancel')),
								onConfirm:    () => resolve(),
							})
						});
					}

					await this.$http.delete(`/companyInfo/${encodeURIComponent(FtgId)}/toggleArchivedState`);

					this.$msgSuccess(Archived ? 'Företaget återfördes från arkiv' : 'Företaget arkiverades.');
					this.$emit('reload');

					if(FtgId === this.FtgInfo?.FtgId) {
						try {
							this.setUpdatedFtgInfo(await this.$http.get(`/companyInfo/${encodeURIComponent(FtgId)}`));
						} catch {}
					}
				},
			});
		},

		onShowCopyFtgDialog(FtgId) {
			this.originalFtgId = FtgId;

			const lastCharacterInFtgId = [...FtgId].pop();
			
			FtgId = isNaN(parseInt(lastCharacterInFtgId, 10)) ? `${FtgId}2` : `${FtgId.slice(0, - 1)}${parseInt(lastCharacterInFtgId, 10) + 1}`;

			this.targetFtgId = `${FtgId}`;
			this.$refs.copyDialog.show();
		},

		onCopy() {
			if(!this.targetFtgId) {
				this.$msgError('Du måste ange ett nytt företags-id.');

				return;
			}

			this.targetFtgId = this.targetFtgId.replace(/[^\d+a-zäåö]/gi, '').replace(/å|ä/gi, 'a').replace(/ö/gi, 'o').toLowerCase();

			if(this.items.find(({FtgId}) => FtgId === this.targetFtgId)) {
				this.$msgError('Det finns redan ett företag med samma id.');

				return;
			}

			if(this.targetFtgId.length > 15) {
				this.$msgError('Företags-id får inte vara längre än 15 tecken.');

				return;
			}

			this.$tryCatch({
				task: async () => {
					this.isCopying = true;

					const {Id} = await this.$http.post('/cloneCompany', {originalFtgId: this.originalFtgId, targetFtgId: this.targetFtgId});

					await new Promise((resolve, reject) => {
						const fetchStatus = async () => {
							const data = await this.$http.get(`/cloneCompany/status/${encodeURIComponent(Id)}`);

							if(!data || data.Status === 'FAILED') {
								reject(new Error('Job Failed'));

								return;
							}

							switch (data.Status) {
								case 'COMPLETED': {
									this.$msgSuccess('Företaget kopierades.');
									this.$refs.copyDialog.hide();
									this.targetFtgId = '';
									this.$emit('reload');
									resolve();

									break;
								}
								case 'INPROGRESS': {
									this.copyStatusMessage = 'Kopierar företaget, vänligen vänta...';
									setTimeout(fetchStatus, 5000);

									break;
								}
								case 'QUEUED': {
									this.copyStatusMessage = 'Din begäran ligger i kö, vänligen vänta...';
									setTimeout(fetchStatus, 2500);

									break;
								}
							}
						};

						fetchStatus();
					});
				},

				finally: () => {
					this.isCopying = false;
				},
			});
		},
		
		toggleFavourite(item) {
			item.isFavourite = LocalStorage.toggleFavouriteCompany(item.FtgId);

			return false;
		},
	},

	computed: {
		...mapState(['UserAccount', 'HasPlus', 'FtgInfo']),

		showRestoreInfo() {
			return process.env.VUE_APP_DEBUG === 'true';
		},

		cloneCompanyEnabled() {
			return this.UserAccount.Roll === 'LIC' && (process.env.VUE_APP_CLONE_COMPANY_WHITELIST || '').split(',').map((LicNr) => LicNr.trim()).includes(this.UserAccount.LicNr);
		},
	},
}
</script>

<style scoped>

</style>
